/**
 * ResumeInviteCheckoutBanner — shown on BillingTab when the org was created
 * via a paid invite code but the user has not yet completed Stripe Checkout.
 *
 * Surfaces when ``subscription.pending_invite_checkout && !hasSubscription``.
 * Replaces the PlanPicker view in that slot so the user is funnelled back
 * into the invite-specific deal (negotiated price, coupon, trial length)
 * rather than offered the catalog plan picker — those terms would be lost
 * if they self-served instead.
 *
 * The CTA calls ``useBilling.resumeInviteCheckout`` which rebuilds the same
 * Stripe Checkout session from the user's redeemed InviteCode and same-tab
 * navigates into it. Idempotent under repeated clicks.
 */

import { CreditCard02 } from '@untitledui/icons';
import { useMemo } from 'react';

import { StatusBanner } from '@/lib/base/banners';

interface ResumeInviteCheckoutBannerProps {
  onResume: () => void;
  isLoading?: boolean;
  errorMessage?: string | null;
}

export function ResumeInviteCheckoutBanner({
  onResume,
  isLoading,
  errorMessage,
}: ResumeInviteCheckoutBannerProps) {
  const action = useMemo(
    () => ({
      label: 'Resume checkout',
      onClick: onResume,
      isLoading,
    }),
    [onResume, isLoading]
  );

  const description = errorMessage
    ? `We couldn't restart checkout: ${errorMessage}`
    : 'Your invite includes pre-arranged pricing. Finish checkout to activate your subscription on those terms.';

  return (
    <div data-component='billing-banner' data-instance='resume-invite-checkout'>
      <StatusBanner
        variant={errorMessage ? 'warning' : 'info'}
        icon={CreditCard02}
        title='Your invite needs payment to activate.'
        description={description}
        action={action}
      />
    </div>
  );
}
